import React from 'react';
import { motion, useReducedMotion, Variants } from 'framer-motion';
interface HalloweekLinkProps {
  isActive: boolean;
  dark: boolean;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  size?: 'sm' | 'lg';
}
const ease = [0.23, 1, 0.32, 1] as const;
export function GhostIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-606" data-mp="3g1qiov7u4zrt">
      <path d="M12 2.5c-4.4 0-7.5 3.3-7.5 7.6v10.6c0 .6.7.9 1.1.5l1.6-1.4 1.7 1.5c.3.3.8.3 1.1 0l1.5-1.5 1.5 1.5c.3.3.8.3 1.1 0l1.7-1.5 1.6 1.4c.4.4 1.1.1 1.1-.5V10.1c0-4.3-3.1-7.6-7.5-7.6Z" fill="#FFF8F0" stroke="#1A1A1A" strokeOpacity="0.35" strokeWidth="1" data-id="element-607" data-mp="hhibkzkwjquj" />
      <ellipse cx="9.4" cy="10.2" rx="1.2" ry="1.6" fill="#1A1A1A" data-id="element-608" data-mp="2khiarn2bnty" />
      <ellipse cx="14.6" cy="10.2" rx="1.2" ry="1.6" fill="#1A1A1A" data-id="element-609" data-mp="2sb4cl9he5pkg" />
      <ellipse cx="12" cy="14" rx="1.1" ry="0.8" fill="#1A1A1A" data-id="element-610" data-mp="2s88ffnddb4x2" />
    </svg>;
}
export function PumpkinIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-611" data-mp="2wqmg4pvibfi0">
      <path d="M12 7.2c.2-1.6.9-2.9 2.3-3.7" stroke="#4A5D23" strokeWidth="1.8" strokeLinecap="round" fill="none" data-id="element-612" data-mp="31oxosxa2fjyn" />
      <path d="M12 7c-1.4-.9-3.3-1-5-.3C4.4 7.7 3 10.4 3 13.6c0 4 2.7 6.9 6.2 6.9 1.1 0 2-.3 2.8-.8.8.5 1.7.8 2.8.8 3.5 0 6.2-2.9 6.2-6.9 0-3.2-1.4-5.9-4-6.9-1.7-.7-3.6-.6-5 .3Z" fill="#FF7A1A" data-id="element-613" data-mp="2mthssmpxddzm" />
      <path d="M12 7.4c-1 1.7-1.4 3.8-1.4 6.2 0 2.4.5 4.5 1.4 6.1M12 7.4c1 1.7 1.4 3.8 1.4 6.2 0 2.4-.5 4.5-1.4 6.1" stroke="#C2410C" strokeWidth="0.9" fill="none" data-id="element-614" data-mp="2ipa1qivzvly8" />
      <path d="m7.4 12.4 1.9-1.1.3 2.1Z M16.6 12.4l-1.9-1.1-.3 2.1Z" fill="#2A1203" data-id="element-615" data-mp="28zh1uf57sv9t" />
      <path d="M8 15.6c1.2 1.1 2.5 1.6 4 1.6s2.8-.5 4-1.6l-1.3.4-.7.7-.8-.6-1.2.7-1.2-.7-.8.6-.7-.7Z" fill="#2A1203" data-id="element-616" data-mp="1uzxuhok89nyk" />
    </svg>;
}
export function HalloweekLink({
  isActive,
  dark,
  onClick,
  size = 'sm'
}: HalloweekLinkProps) {
  const reduceMotion = useReducedMotion();
  const transition = reduceMotion ? {
    duration: 0
  } : {
    duration: 0.25,
    ease
  };
  const ghost: Variants = {
    rest: {
      opacity: 0,
      y: 8,
      x: 6,
      rotate: -12,
      scale: 0.85
    },
    shown: {
      opacity: 1,
      y: -3,
      x: 0,
      rotate: -8,
      scale: 1,
      transition
    }
  };
  const pumpkin: Variants = {
    rest: {
      opacity: 0,
      y: 8,
      scale: 0.85
    },
    shown: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: reduceMotion ? transition : {
        ...transition,
        delay: 0.05
      }
    }
  };
  const iconSize = size === 'lg' ? 'w-5 h-5' : 'w-4 h-4';
  const color = isActive ? 'text-[#FF7A1A]' : dark ? 'text-[#F5EDE3]/75 hover:text-[#FF9A4D]' : 'text-[#1A1A1A]/70 hover:text-[#C2410C]';
  return <motion.a href="/events" onClick={onClick} aria-current={isActive ? 'page' : undefined} initial={isActive || size === 'lg' ? 'shown' : 'rest'} animate={isActive || size === 'lg' ? 'shown' : 'rest'} whileHover="shown" whileFocus="shown" className={`relative inline-flex items-center gap-1.5 font-semibold tracking-wide transition-colors ${size === 'lg' ? 'text-base' : 'text-sm'} ${color}`} data-id="element-617" data-mp="2z9rakxir6riy">
      <motion.span variants={ghost} className={`absolute -left-[18px] top-1/2 -mt-2.5 pointer-events-none ${size === 'lg' ? 'static mt-0' : ''}`} data-id="element-618" data-mp="1jq038ci50wvw">
        <GhostIcon className={iconSize} data-id="element-619" data-mp="1th4htb3wifq7" />
      </motion.span>
      Halloweek
      <motion.span variants={pumpkin} className={`absolute -right-[18px] top-1/2 -mt-2 pointer-events-none ${size === 'lg' ? 'static mt-0' : ''}`} data-id="element-620" data-mp="1yfo2772ujjgg">
        <PumpkinIcon className={iconSize} data-id="element-621" data-mp="22i2q45ldh9el" />
      </motion.span>
    </motion.a>;
}